import { useEffect, useState } from 'react';
import { Upload, Button, Drawer } from 'antd';
import { ReactComponent as UploadIcon } from '../../assets/images/upload.svg';
import useStyle from './index.styles';
import { UploadRequestOption as RcCustomRequestOptions } from 'rc-upload/lib/interface';
import type { RcFile, UploadChangeParam, UploadFile, UploadProps } from 'antd/es/upload/interface';
import imageIcon from '../../assets/images/fileLoadingPlaceholder.png';
import videoIcon from '../../assets/images/videoLoadingPlaceholder.svg';
import fileIcon from '../../assets/images/uploadedFile.svg';
import { TriangleExclamationSolid, XmarkSolid, TrashSolid } from '../../utils/icons';
import update from 'immutability-helper';
import { getBase64 } from '../../utils';
import Modal from 'antd/es/modal/Modal';
import { useTranslation } from 'react-i18next';
import ImgCrop from 'antd-img-crop';
import { useIsMobile } from '../../utils/hooks';

const FileToShow = (props: UploadFile & { deleteImage: () => void; maxFileSizeInMb: number }) => {
  const { deleteImage, maxFileSizeInMb, name, originFileObj, size, status, type, uid, url } = props;
  const isImage = type?.includes('image');
  const isVideo = type?.includes('video');
  const isFile = !isImage && !isVideo;
  const [uploadedFile, setUploadedFile] = useState<string>(
    isImage ? imageIcon : isVideo ? videoIcon : fileIcon
  );
  const [showFileInModal, setShowFileInModal] = useState<boolean>(false);
  const fileType = type?.split('/')[1].toLocaleUpperCase();
  const fileSize = size ? (size / 1000000).toFixed(1) : '';
  const boxStyles =
    'flex items-center flex-1 p-[16px] border border-[1px] border-bg2 rounded-[8px] box-border border-solid min-h-[74px] bg-bg4';
  const classes = useStyle();
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  useEffect(() => {
    const getUrl = async () => {
      const url = await getBase64(originFileObj as RcFile);
      setUploadedFile(url as string);
    };

    if (status === 'done' && originFileObj) {
      getUrl();
    } else if (url) {
      setUploadedFile(url);
    }
  }, [originFileObj, status, uid, url]);

  const getModalContent = () => (
    <>
      {isImage && <img alt="images" style={{ width: '100%' }} src={uploadedFile} />}
      {isVideo && (
        <video controls style={{ width: '100%' }}>
          <source type="video/mp4" src={uploadedFile} />
        </video>
      )}
      {isFile && (
        <object className="min-h-[400px] w-[100%]" aria-label="file" data={uploadedFile}></object>
      )}
    </>
  );

  switch (status) {
    case 'uploading': {
      return (
        <div className={boxStyles}>
          <img src={uploadedFile} alt="" className="mr-[8px] h-[32px] w-[32px]" />
          <div className="flex flex-1 flex-col overflow-auto">
            <p className={`smallParagraphBold truncate text-text2`}>{name}</p>
          </div>
          <Button ghost className={`${classes.xMark}`} onClick={deleteImage}>
            <XmarkSolid />
          </Button>
        </div>
      );
    }

    case 'error': {
      return (
        <div className={boxStyles}>
          <div
            className={`mr-[8px] flex h-[32px] w-[32px] items-center justify-center rounded-[32px] bg-[#FFECEC] ${classes.triangleExclamation}`}
          >
            <TriangleExclamationSolid />
          </div>
          <div className="flex flex-1 flex-col overflow-auto">
            <p className={`smallParagraphBold truncate text-text2`}>{name}</p>
            <p className={`smallParagraphBold text-danger`}>
              {t('upload.fileIsVeryLarge', { value: maxFileSizeInMb })}
            </p>
          </div>
          <Button ghost className={`${classes.xMark}`} onClick={deleteImage}>
            <XmarkSolid />
          </Button>
        </div>
      );
    }

    case 'done': {
      return (
        <div className={boxStyles}>
          <img
            src={isImage ? uploadedFile : isVideo ? videoIcon : fileIcon}
            alt=""
            className="mr-[8px] h-[32px] w-[32px] min-w-[32px] cursor-pointer rounded-[50%]"
            onClick={() => setShowFileInModal(true)}
          />
          <div className="flex flex-1 flex-col overflow-auto">
            <p className={`smallParagraphBold truncate text-text2`}>{name}</p>
            {url ? (
              <p className="smallParagraph">{`${fileType}`}</p>
            ) : (
              <p className="smallParagraph">{`${fileType}, ${fileSize} Mb`}</p>
            )}
          </div>
          <Button ghost className={`${classes.xMark}`} onClick={deleteImage}>
            <XmarkSolid />
          </Button>
          {!isMobile && (
            <Modal
              open={showFileInModal}
              title={name}
              footer={null}
              onCancel={() => setShowFileInModal(false)}
              closeIcon={<XmarkSolid />}
              centered
            >
              {getModalContent()}
            </Modal>
          )}
          {isMobile && (
            <Drawer
              title={name}
              placement="bottom"
              onClose={() => setShowFileInModal(false)}
              open={showFileInModal}
              closeIcon={<XmarkSolid />}
              rootClassName="drawerMobile"
              size="large"
            >
              {getModalContent()}
            </Drawer>
          )}
        </div>
      );
    }

    default:
      return <></>;
  }
};

const DLSUpload = (
  props: UploadProps & {
    setFileList: Function;
    fileList: UploadFile[];
    showImageCrop?: boolean;
    maxFileSizeInMb: number;
    maxCount: number;
  }
) => {
  const {
    accept,
    disabled,
    fileList,
    maxCount,
    multiple,
    setFileList,
    showImageCrop,
    maxFileSizeInMb,
  } = props;
  const classes = useStyle();
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const allowedFiles = [];
  if (accept?.includes('png')) allowedFiles.push('PNG');
  if (accept?.includes('jpeg')) allowedFiles.push('JPG');
  if (accept?.includes('mp4')) allowedFiles.push('MP4');
  if (accept?.includes('pdf')) allowedFiles.push('PDF');

  const handleChange = (info: UploadChangeParam) => {
    const {
      fileList,
      file: { uid, size, status },
    } = info;
    if (status === 'done' && size && size / 1000000 >= maxFileSizeInMb) {
      const index = fileList.findIndex((e) => e.uid === uid);
      setFileList(
        update(fileList, {
          [index]: {
            status: { $set: 'error' },
          },
        })
      );
    } else {
      setFileList(fileList.map((e) => ({ ...e, name: e.name.substring(0, 250) })));
    }
  };

  return (
    <div className="flex flex-col">
      <ImgCrop
        modalTitle={String(t('upload.editImage'))}
        resetText={String(t('upload.undoChanges'))}
        modalOk={String(t('common.save'))}
        modalCancel={String(t('common.cancel'))}
        showGrid
        showReset={!isMobile}
        rotationSlider
        modalProps={{ closeIcon: <XmarkSolid /> }}
        beforeCrop={() => (showImageCrop ? Promise.resolve(true) : Promise.reject())}
      >
        <Upload
          className={`w-[100%] cursor-pointer ${classes.upload}`}
          {...props}
          customRequest={(options: RcCustomRequestOptions) => {
            setTimeout(() => {
              if (options?.onSuccess) {
                options.onSuccess('ok');
              }
            }, 0);
          }}
          showUploadList={false}
          onChange={handleChange}
          fileList={fileList}
        >
          {(!maxCount || maxCount > fileList.length) && (
            <>
              <div className="box-border hidden w-[100%] items-center justify-between rounded-[8px] border border-dashed border-primary bg-bg4 p-[16px] md:flex">
                <p className="smallParagraph text-text3">{t('upload.dragAFile')}</p>
                <Button
                  className={`flex items-center ${classes.buttonWithIcon}`}
                  disabled={disabled}
                >
                  {t('upload.uploadFile')} <UploadIcon className="ml-[4px]" />
                </Button>
              </div>
              <div className="block md:hidden">
                <Button
                  className={`flex w-[100%] items-center justify-center ${classes.buttonWithIcon}`}
                  disabled={disabled}
                >
                  {t('upload.uploadFile')} <UploadIcon className="ml-[4px]" />
                </Button>
              </div>
              {maxCount === 1 && (
                <p className="smallParagraph mt-[4px] text-text2">
                  {t('upload.allowedFiles')}
                  {allowedFiles.map((allowedFile: string, index: number) => (
                    <span key={allowedFile}>
                      {allowedFile}
                      {index !== allowedFiles.length - 1 &&
                        (index === allowedFiles.length - 2 ? ` ${t('upload.and')} ` : ', ')}
                    </span>
                  ))}
                  {` - ${maxFileSizeInMb} MB`}
                </p>
              )}
              {!!(maxCount && maxCount > 1) && (
                <p className="smallParagraph mt-[4px] text-text2">
                  {t('upload.allowedFilesNumber', { value: maxCount })}
                  {` ${maxFileSizeInMb} MB ${t('upload.eachOne')} - ${t('upload.format')} `}

                  {allowedFiles.map((allowedFile: string, index: number) => (
                    <span key={allowedFile}>
                      {allowedFile}
                      {index !== allowedFiles.length - 1 &&
                        (index === allowedFiles.length - 2 ? ` ${t('upload.and')} ` : ', ')}
                    </span>
                  ))}
                </p>
              )}
            </>
          )}
        </Upload>
      </ImgCrop>
      {multiple && fileList.length > 0 && (
        <p className="paragraphBold mb-[16px] mt-[32px] md:mt-[24px]">
          {t('upload.uploadedFiles')}
        </p>
      )}
      {fileList.map((file, index) => (
        <div key={`image-id-${file.uid}`} className="mb-[8px] last:mb-[0px]">
          <FileToShow
            {...file}
            deleteImage={() => setFileList(update(fileList, { $splice: [[index, 1]] }))}
            maxFileSizeInMb={maxFileSizeInMb}
          />
        </div>
      ))}
      {multiple && fileList.length > 0 && (
        <div>
          <Button
            className={`float-right flex items-center ${classes.buttonWithIcon} small`}
            onClick={() => setFileList([])}
          >
            {t('upload.deleteAll')}
            <TrashSolid className="ml-[6px]" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default DLSUpload;
